import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';
import { Star, MessageSquare, CheckCircle, Sparkles, TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import api from '../../utils/api';

export function ProductReviews({ productId }) {
  const { user } = useCart();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canReview, setCanReview] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ratingBreakdown, setRatingBreakdown] = useState({});
  const [totalReviews, setTotalReviews] = useState(0);
  const [aiSummary, setAiSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    comment: '',
  });

  useEffect(() => {
    fetchReviews();
    fetchAISummary();
    if (user) {
      checkCanReview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, user]);

  const fetchReviews = async () => {
    try {
      const data = await api.getProductReviews(productId);
      setReviews(data.reviews || []);
      setRatingBreakdown(data.rating_breakdown || {});
      setTotalReviews(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAISummary = async () => {
    setSummaryLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/review-summary/${productId}`
      );
      const data = await response.json();
      setAiSummary(data);
    } catch (error) {
      console.error('Failed to fetch AI summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const checkCanReview = async () => {
    try {
      const data = await api.canReview(productId, user.phone);
      setCanReview(data.can_review);
    } catch (error) {
      console.error('Failed to check review eligibility:', error);
    }
  };

  const handleSubmitReview = async () => {
    if (!user) {
      toast.error('Please login to submit a review');
      return;
    }

    setSubmitting(true);
    try {
      const result = await api.createReview({
        product_id: productId,
        rating: reviewForm.rating,
        title: reviewForm.title || null,
        comment: reviewForm.comment || null,
        phone: user.phone,
      });

      if (result.success) {
        toast.success('Review submitted successfully!');
        setShowReviewDialog(false);
        setReviewForm({ rating: 5, title: '', comment: '' });
        fetchReviews();
        setCanReview(false);
      } else {
        toast.error(result.detail || 'Failed to submit review');
      }
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ rating, interactive = false, onChange }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-5 h-5 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} ${interactive ? 'cursor-pointer' : ''}`}
          onClick={() => interactive && onChange && onChange(star)}
        />
      ))}
    </div>
  );

  const averageRating = totalReviews > 0 
    ? Object.entries(ratingBreakdown).reduce((sum, [rating, count]) => sum + rating * count, 0) / totalReviews 
    : 0;

  if (loading) {
    return (
      <div className="mt-8">
        <div className="h-8 w-48 bg-muted rounded animate-pulse mb-4" />
        <div className="space-y-4">
          {[1, 2].map(i => (
            <div key={i} className="h-24 bg-muted rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8" data-testid="product-reviews">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Customer Reviews</h2>
          {totalReviews > 0 && (
            <div className="flex items-center gap-2 mt-1">
              <StarRating rating={Math.round(averageRating)} />
              <span className="text-sm text-muted-foreground">
                {averageRating.toFixed(1)} out of 5 ({totalReviews} reviews)
              </span>
            </div>
          )}
        </div>
        
        {canReview && (
          <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
            <DialogTrigger asChild>
              <Button data-testid="write-review-btn">Write a Review</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Write a Review</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Your Rating</Label>
                  <div className="mt-2">
                    <StarRating 
                      rating={reviewForm.rating} 
                      interactive 
                      onChange={(rating) => setReviewForm({ ...reviewForm, rating })} 
                    />
                  </div>
                </div>
                <div>
                  <Label>Title (optional)</Label>
                  <Input
                    value={reviewForm.title}
                    onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                    placeholder="Sum up your experience"
                  />
                </div>
                <div>
                  <Label>Review (optional)</Label>
                  <Textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    placeholder="Tell others about your experience"
                    rows={4}
                  />
                </div>
                <Button 
                  onClick={handleSubmitReview} 
                  disabled={submitting}
                  className="w-full"
                >
                  {submitting ? 'Submitting...' : 'Submit Review'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* AI-Powered Review Summary */}
      {aiSummary && aiSummary.total_reviews > 0 && (
        <Card className="mb-6 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 border-purple-200 dark:border-purple-800">
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-base sm:text-lg">AI Review Summary</h3>
              <Badge variant="secondary" className="text-xs">
                {aiSummary.total_reviews} reviews analyzed
              </Badge>
            </div>

            {summaryLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Analyzing reviews...
              </div>
            ) : (
              <div className="space-y-3">
                {/* Overall Summary */}
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
                  {aiSummary.summary}
                </p>

                {/* Positive Points */}
                {aiSummary.positive_points && aiSummary.positive_points.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-semibold text-green-700 dark:text-green-400">
                        What customers love:
                      </span>
                    </div>
                    <ul className="space-y-1 ml-6">
                      {aiSummary.positive_points.map((point, idx) => (
                        <li key={idx} className="text-sm text-gray-600 dark:text-gray-400 list-disc">
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Negative Points */}
                {aiSummary.negative_points && aiSummary.negative_points.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingDown className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                        Areas to consider:
                      </span>
                    </div>
                    <ul className="space-y-1 ml-6">
                      {aiSummary.negative_points.map((point, idx) => (
                        <li key={idx} className="text-sm text-gray-600 dark:text-gray-400 list-disc">
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Overall Sentiment Badge */}
                <div className="flex items-center gap-2 pt-2 border-t border-purple-200 dark:border-purple-800">
                  <span className="text-xs font-medium text-muted-foreground">Overall Sentiment:</span>
                  <Badge 
                    variant={
                      aiSummary.overall_sentiment === 'positive' ? 'default' : 
                      aiSummary.overall_sentiment === 'negative' ? 'destructive' : 
                      'secondary'
                    }
                    className="text-xs"
                  >
                    {aiSummary.overall_sentiment}
                  </Badge>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Rating Breakdown */}
      {totalReviews > 0 && (
        <Card className="p-4 mb-6">
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map(rating => {
              const count = ratingBreakdown[rating] || 0;
              const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
              return (
                <div key={rating} className="flex items-center gap-2 text-sm">
                  <span className="w-3">{rating}</span>
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-400 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="w-8 text-muted-foreground">{count}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <Card className="p-8 text-center">
          <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-medium mb-2">No reviews yet</h3>
          <p className="text-sm text-muted-foreground">
            {canReview ? 'Be the first to review this product!' : 'Purchase this product to leave a review'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <Card key={review.id} className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <StarRating rating={review.rating} />
                    {review.is_verified_purchase && (
                      <span className="flex items-center gap-1 text-xs text-green-600">
                        <CheckCircle className="w-3 h-3" />
                        Verified Purchase
                      </span>
                    )}
                  </div>
                  {review.title && (
                    <h4 className="font-medium">{review.title}</h4>
                  )}
                  {review.comment && (
                    <p className="text-sm text-muted-foreground mt-1">{review.comment}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    {review.user_name} • {new Date(review.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default ProductReviews;
